import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Camera, Loader2, Save, User as UserIcon } from "lucide-react";
import api from "@/lib/api";
import { compressImage } from "@/lib/compress";
import { useAuth } from "@/context/AuthContext";

/**
 * Profile edit modal — the logged-in user (any role) edits their display
 * name + phone and uploads a profile photo from device / gallery.
 * Backed by PATCH /auth/me and POST /auth/me/avatar.
 */
export default function ProfileModal({ open, onClose }) {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (open && user) {
      setForm({ name: user.name || "", phone: user.phone || "" });
      setPreview(user.avatar_url || null);
    }
  }, [open, user]);

  if (!open) return null;

  const save = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) { toast.error("Name must be at least 2 characters"); return; }
    setBusy(true);
    try {
      await api.patch("/auth/me", { name: form.name.trim(), phone: form.phone.trim() });
      await refresh();
      toast.success("Profile updated");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const uploadAvatar = async (rawFile) => {
    if (!rawFile) return;
    if (!rawFile.type?.startsWith("image/")) { toast.error("Only image files"); return; }
    setUploading(true);
    // Layer 1: shrink avatar client-side so mobile never uploads 20 MB HEIC
    const file = await compressImage(rawFile, "avatar");
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data.avatar_url);
      await refresh();
      toast.success("Photo updated");
    } catch (e) {
      setPreview(user?.avatar_url || null);
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); URL.revokeObjectURL(localUrl); }
  };

  const initial = (form.name || user?.name || "U").trim()[0]?.toUpperCase() || "U";

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="profile-modal">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest uppercase font-black text-ehi-accent">MY PROFILE</div>
            <h3 className="font-display font-black text-lg text-ehi-ink tracking-tight">Edit your profile</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-sm" data-testid="profile-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-5 space-y-5">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-ehi-primary text-white grid place-items-center text-2xl font-bold ring-2 ring-slate-200">
                {preview ? (
                  <img src={preview} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar-preview" />
                ) : (
                  <span>{initial}</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-ehi-accent text-white grid place-items-center shadow-lg hover:bg-orange-600 transition-colors disabled:opacity-60"
                title="Upload from gallery / storage"
                data-testid="profile-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={(e) => uploadAvatar(e.target.files?.[0])}
                data-testid="profile-avatar-input"
              />
            </div>
            <div className="text-xs text-slate-500 leading-relaxed">
              <div className="font-bold text-slate-700 mb-0.5">Profile photo</div>
              JPG, PNG, GIF or WebP · max 5 MB.<br />Stored securely — used across your dashboard.
            </div>
          </div>

          <label className="block">
            <span className="text-[10px] tracking-widest uppercase font-black text-slate-500 block mb-1">Full name</span>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Ram Singh Chauhan"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-ehi-primary outline-none"
              data-testid="profile-name-input"
            />
          </label>

          <label className="block">
            <span className="text-[10px] tracking-widest uppercase font-black text-slate-500 block mb-1">Phone</span>
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+91 …"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-ehi-primary outline-none"
              data-testid="profile-phone-input"
            />
          </label>

          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 flex items-start gap-2">
            <UserIcon className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
            <div>
              Signed in as <b className="text-slate-700">{user?.email}</b> · role <b className="text-slate-700">{user?.role}</b><br />
              Email &amp; role can only be changed by an admin.
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-600 rounded-sm hover:bg-slate-100" data-testid="profile-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-accent inline-flex items-center gap-1.5" data-testid="profile-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
